import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { useCustomDialog } from '../context/DialogContext';
import '../styles/superadmin.css';

const BASE = 'https://menu.searchisend.in';

const ManageQRTemplates = () => {
    const navigate = useNavigate();
    const { alert: customAlert, confirm: customConfirm } = useCustomDialog();
    const token = localStorage.getItem('saas_superadmin_token');
    
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [templateName, setTemplateName] = useState('');

    // Editor State
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [pos, setPos] = useState(null);
    const [savingPos, setSavingPos] = useState(false);

    // Image Reference for scaling
    const imgRef = useRef(null);
    const [scale, setScale] = useState(0);
    const [interactLoaded, setInteractLoaded] = useState(false);

    useEffect(() => {
        if (!token) {
            navigate('/superadmin_v2_secure_login');
            return;
        }
        fetchTemplates();
        
        // Dynamically load interact.js
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/interactjs/dist/interact.min.js';
        script.onload = () => setInteractLoaded(true);
        document.body.appendChild(script);
        
        return () => {
            document.body.removeChild(script);
        }
        // eslint-disable-next-line
    }, []);

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_qr.php?action=fetch_templates`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setTemplates(data.templates);
            } else {
                await customAlert(data.message || 'Error fetching templates', 'Error', 'danger');
            }
        } catch (err) {
            console.error('Error fetching templates', err);
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        const fileInput = document.getElementById('template_image');
        if (!fileInput.files[0] || !templateName) {
            await customAlert('Please provide a name and select an image.', 'Validation Error', 'warning');
            return;
        }
        setUploading(true);
        const formData = new FormData();
        formData.append('template_name', templateName);
        formData.append('template_image', fileInput.files[0]);

        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_qr.php?action=upload_template`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await res.json();
            if (data.success) {
                await customAlert('Template uploaded successfully!', 'Uploaded', 'success');
                setTemplateName('');
                fileInput.value = '';
                
                // Fetch the updated list
                const resList = await fetch(`${BASE}/apimenu/v2/v2_super_qr.php?action=fetch_templates`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const dataList = await resList.json();
                if (dataList.success) {
                    setTemplates(dataList.templates);
                    // Automatically open the editor for the newly created template
                    const newTemplate = dataList.templates.find(t => String(t.id) === String(data.template_id));
                    if (newTemplate) {
                        openEditor(newTemplate);
                    }
                }
            } else {
                await customAlert(data.message || 'Error uploading template', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error during upload', 'Error', 'danger');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!await customConfirm("Are you sure you want to delete this template?", "Delete Template", "danger")) return;
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_qr.php?action=delete_template`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ template_id: id })
            });
            const data = await res.json();
            if (data.success) {
                fetchTemplates();
            } else {
                await customAlert(data.message || 'Error deleting template', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error during deletion', 'Error', 'danger');
        }
    };

    const openEditor = (template) => {
        setEditingTemplate(template);
        setScale(0); // Reset scale on open
        setPos({
            qr_x: Number(template.qr_x_position) || 50,
            qr_y: Number(template.qr_y_position) || 100,
            qr_size: Number(template.qr_size) || 150,
            logo_x: Number(template.logo_x_position) || 50,
            logo_y: Number(template.logo_y_position) || 20,
            logo_size: Number(template.logo_size) || 50,
            name_x: Number(template.name_x_position) || 50,
            name_y: Number(template.name_y_position) || 80,
            name_font_size: Number(template.name_font_size) || 20,
            name_font_color: template.name_font_color || '#ffffff'
        });
    };

    const closeEditor = () => {
        setEditingTemplate(null);
        setPos(null);
        setScale(0);
    };

    const handlePosChange = (field, value) => {
        setPos(prev => ({ ...prev, [field]: value }));
    };

    const downloadPreview = async () => {
        if (!imgRef.current || !pos) return;
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = imgRef.current.naturalWidth;
        canvas.height = imgRef.current.naturalHeight;
        
        const drawOverlaysAndDownload = () => {
            // Draw QR Placeholder
            ctx.fillStyle = 'rgba(249, 115, 22, 0.4)';
            ctx.fillRect(pos.qr_x, pos.qr_y, pos.qr_size, pos.qr_size);
            ctx.strokeStyle = 'rgba(249, 115, 22, 1)';
            ctx.lineWidth = 4;
            ctx.strokeRect(pos.qr_x, pos.qr_y, pos.qr_size, pos.qr_size);
            ctx.fillStyle = 'white';
            ctx.font = `bold ${pos.qr_size * 0.2}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('QR CODE', pos.qr_x + pos.qr_size/2, pos.qr_y + pos.qr_size/2);

            // Draw Logo Placeholder
            ctx.fillStyle = 'rgba(59, 130, 246, 0.4)';
            ctx.beginPath();
            ctx.arc(pos.logo_x + pos.logo_size/2, pos.logo_y + pos.logo_size/2, pos.logo_size/2, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = 'rgba(59, 130, 246, 1)';
            ctx.stroke();
            ctx.fillStyle = 'white';
            ctx.font = `bold ${pos.logo_size * 0.2}px Arial`;
            ctx.fillText('LOGO', pos.logo_x + pos.logo_size/2, pos.logo_y + pos.logo_size/2);

            // Draw Name Placeholder
            ctx.fillStyle = pos.name_font_color;
            ctx.font = `bold ${pos.name_font_size}px Arial`;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
            ctx.fillText('Restaurant Name', pos.name_x, pos.name_y);

            try {
                // Trigger download
                const link = document.createElement('a');
                link.download = `preview_${editingTemplate.template_name.replace(/\s+/g, '_')}.png`;
                link.href = canvas.toDataURL('image/png');
                link.click();
            } catch (e) {
                customAlert('Preview download was blocked by browser CORS security (because you are testing on localhost). This feature will work perfectly in production.', 'CORS Warning', 'warning');
                console.error('Canvas taint error:', e);
            }
        };

        try {
            // Fetch the image as a blob through our authenticated API proxy to bypass CORS completely
            const proxyUrl = `${BASE}/apimenu/v2/v2_super_qr.php?action=fetch_image_blob&url=${encodeURIComponent(editingTemplate.image_url)}`;
            const res = await fetch(proxyUrl, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!res.ok) throw new Error('Failed to fetch image via proxy');
            
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const img = new Image();
            img.onload = () => {
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                drawOverlaysAndDownload();
                URL.revokeObjectURL(url);
            };
            img.onerror = () => { throw new Error('Blob load failed'); };
            img.src = url;
        } catch (err) {
            // Fallback: draw DOM image directly (will taint canvas if cross-origin)
            try {
                ctx.drawImage(imgRef.current, 0, 0, canvas.width, canvas.height);
                drawOverlaysAndDownload();
            } catch (fallbackErr) {
                console.error("Critical error drawing to canvas:", fallbackErr);
            }
        }
    };

    const setupEditor = () => {
        try {
            if (!imgRef.current || !interactLoaded || !pos) return;
            
            const tempImg = new Image();
            tempImg.onload = () => {
                try {
                    const originalWidth = tempImg.width || 1;
                    
                    // Create ResizeObserver to watch for layout shifts (e.g. sidebar toggling)
                    const resizeObserver = new ResizeObserver(entries => {
                        for (let entry of entries) {
                            const renderedWidth = entry.contentRect.width;
                            if (renderedWidth > 0) {
                                const scaleRatio = originalWidth / renderedWidth;
                                setScale(scaleRatio);
                            }
                        }
                    });
                    
                    resizeObserver.observe(imgRef.current);
                    // Save observer to a ref so we can disconnect it on cleanup
                    if (window._qrResizeObserver) {
                        window._qrResizeObserver.disconnect();
                    }
                    window._qrResizeObserver = resizeObserver;

                    const scaleRatio = originalWidth / imgRef.current.clientWidth;
                    setScale(scaleRatio);

                    const initializeElement = (elementId) => {
                        const target = document.getElementById(elementId);
                        if (!target) return;
                        
                        const interact = window.interact;
                        if (!interact) return;
                        
                        try { interact(target).unset(); } catch (e) {}

                        const interaction = interact(target)
                            .draggable({
                                listeners: {
                                    move(event) {
                                        // Use the CURRENT scale, not the closure scaleRatio
                                        setScale(currentScale => {
                                            setPos(prev => {
                                                if(elementId === 'draggable-qr') return {...prev, qr_x: Math.round(prev.qr_x + (event.dx * currentScale)), qr_y: Math.round(prev.qr_y + (event.dy * currentScale))};
                                                if(elementId === 'draggable-logo') return {...prev, logo_x: Math.round(prev.logo_x + (event.dx * currentScale)), logo_y: Math.round(prev.logo_y + (event.dy * currentScale))};
                                                if(elementId === 'draggable-name') return {...prev, name_x: Math.round(prev.name_x + (event.dx * currentScale)), name_y: Math.round(prev.name_y + (event.dy * currentScale))};
                                                return prev;
                                            });
                                            return currentScale;
                                        });
                                    }
                                },
                                modifiers: interact.modifiers ? [interact.modifiers.restrictRect({ restriction: 'parent' })] : []
                            });

                        if (elementId !== 'draggable-name') {
                            interaction.resizable({
                                edges: { left: true, right: true, bottom: true, top: true },
                                listeners: {
                                    move: function (event) {
                                        setScale(currentScale => {
                                            setPos(prev => {
                                                if(elementId === 'draggable-qr') return {...prev, qr_size: Math.round(event.rect.width * currentScale)};
                                                if(elementId === 'draggable-logo') return {...prev, logo_size: Math.round(event.rect.width * currentScale)};
                                                return prev;
                                            });
                                            return currentScale;
                                        });
                                    }
                                },
                                modifiers: interact.modifiers ? [ interact.modifiers.restrictSize({ min: { width: 30 } }) ] : [] // Removed currentScale from modifiers because it's evaluated once
                            });
                        }
                    };

                    initializeElement('draggable-qr');
                    initializeElement('draggable-logo');
                    initializeElement('draggable-name');
                } catch (innerError) {
                    console.error('Error during setupEditor image onload logic:', innerError);
                }
            };
            tempImg.src = imgRef.current.src;
        } catch (outerError) {
            console.error('Error in setupEditor execution:', outerError);
        }
    };

    // Cleanup observer on unmount or editor close
    useEffect(() => {
        return () => {
            if (window._qrResizeObserver) {
                window._qrResizeObserver.disconnect();
                window._qrResizeObserver = null;
            }
        };
    }, []);

    useEffect(() => {
        if (editingTemplate && interactLoaded) {
            const timer = setTimeout(() => {
                if (imgRef.current && imgRef.current.complete) {
                    setupEditor();
                }
            }, 300);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line
    }, [editingTemplate, interactLoaded]);


    const savePositions = async () => {
        setSavingPos(true);
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_qr.php?action=save_position`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    template_id: editingTemplate.id,
                    ...pos
                })
            });
            const data = await res.json();
            if (data.success) {
                await customAlert('Positions saved successfully!', 'Saved', 'success');
                fetchTemplates(); // Refresh background data
                closeEditor();
            } else {
                await customAlert(data.message || 'Error saving positions', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error while saving positions', 'Error', 'danger');
        } finally {
            setSavingPos(false);
        }
    };

    return (
        <SuperAdminLayout>
            {!editingTemplate ? (
                        <>
                            <h2 className="sa-page-title">Manage QR Templates</h2>
                            <p className="sa-page-subtitle">Upload new template designs. Position the QR code, logo, and name dynamically.</p>

                            <div className="sa-panel" style={{ marginBottom: '2rem' }}>
                                <h3 className="sa-panel-title"><i className="fas fa-upload"></i> Upload New Template</h3>
                                <form onSubmit={handleUpload} style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                    <div className="sa-form-group" style={{ flex: 1, minWidth: '200px' }}>
                                        <label className="sa-form-label">Template Name</label>
                                        <input type="text" className="sa-input" style={{ width: '100%' }} value={templateName} onChange={(e) => setTemplateName(e.target.value)} required placeholder="e.g. Dark Mode Theme" />
                                    </div>
                                    <div className="sa-form-group" style={{ flex: 2, minWidth: '300px' }}>
                                        <label className="sa-form-label">Background Image (PNG/JPG)</label>
                                        <input type="file" id="template_image" accept="image/png, image/jpeg" className="sa-input" style={{ width: '100%' }} required />
                                    </div>
                                    <button type="submit" disabled={uploading} className="sa-primary-btn" style={{ marginBottom: '15px' }}>
                                        {uploading ? <><i className="fas fa-spinner fa-spin"></i> Uploading...</> : <><i className="fas fa-plus"></i> Upload Template</>}
                                    </button>
                                </form>
                            </div>

                            <div className="sa-table-container">
                                <table className="sa-table">
                                    <thead>
                                        <tr>
                                            <th>Preview</th>
                                            <th>Name</th>
                                            <th>Date Added</th>
                                            <th style={{ textAlign: 'right' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr><td colSpan="4" style={{ padding: '4rem', textAlign: 'center', color: 'var(--text-secondary)' }}><i className="fas fa-circle-notch fa-spin fa-2x"></i></td></tr>
                                        ) : templates.length === 0 ? (
                                            <tr><td colSpan="4" style={{ padding: '4rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No templates uploaded yet.</td></tr>
                                        ) : templates.map((template) => (
                                            <tr key={template.id}>
                                                <td>
                                                    <img src={template.image_url} alt="Preview" style={{ width: '80px', height: '120px', objectFit: 'contain', border: '1px solid var(--border-color)', borderRadius: '4px', background: '#f8fafc' }} />
                                                </td>
                                                <td>
                                                    <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{template.template_name}</div>
                                                </td>
                                                <td>{new Date(template.created_at).toLocaleDateString()}</td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                                        <button onClick={() => openEditor(template)} className="sa-primary-btn" style={{ background: 'var(--info)' }}>
                                                            <i className="fas fa-crosshairs"></i> Position Elements
                                                        </button>
                                                        <button onClick={() => handleDelete(template.id)} className="sa-action-btn danger" title="Delete Template">
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    ) : (
                        // EDITOR MODE
                        <div className="editor-mode">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                <div>
                                    <h2 className="sa-page-title">Visual Editor: {editingTemplate.template_name}</h2>
                                    <p className="sa-page-subtitle">Adjust the X/Y values and sizes below to correctly position elements on the background.</p>
                                </div>
                                <button onClick={closeEditor} className="sa-secondary-btn"><i className="fas fa-times"></i> Cancel</button>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '30px', alignItems: 'center' }}>
                                {/* Preview Area */}
                                <div style={{ position: 'relative', width: '100%', maxWidth: '600px', border: '2px dashed var(--border-color)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: '#f1f5f9' }}>
                                    <img 
                                        ref={imgRef} 
                                        src={editingTemplate.image_url} 
                                        alt="Template" 
                                        onLoad={setupEditor}
                                        style={{ width: '100%', display: 'block', height: 'auto' }} 
                                    />
                                    
                                    {/* Virtual Overlays bound to React state */}
                                    {pos && scale > 0 && (
                                        <>
                                            <div id="draggable-qr" className="draggable-element" style={{
                                                position: 'absolute',
                                                top: 0, left: 0,
                                                transform: `translate(${pos.qr_x / scale}px, ${pos.qr_y / scale}px)`,
                                                width: `${pos.qr_size / scale}px`,
                                                height: `${pos.qr_size / scale}px`,
                                                border: '2px dashed rgba(249, 115, 22, 1)',
                                                background: 'rgba(249, 115, 22, 0.4)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: 'white', fontWeight: 'bold', textShadow: '0px 1px 2px rgba(0,0,0,0.8)',
                                                cursor: 'move', touchAction: 'none'
                                            }}>QR</div>

                                            <div id="draggable-logo" className="draggable-element" style={{
                                                position: 'absolute',
                                                top: 0, left: 0,
                                                transform: `translate(${pos.logo_x / scale}px, ${pos.logo_y / scale}px)`,
                                                width: `${pos.logo_size / scale}px`,
                                                height: `${pos.logo_size / scale}px`,
                                                border: '2px dashed rgba(59, 130, 246, 1)',
                                                background: 'rgba(59, 130, 246, 0.4)',
                                                borderRadius: '50%',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: 'white', fontWeight: 'bold', textShadow: '0px 1px 2px rgba(0,0,0,0.8)',
                                                cursor: 'move', touchAction: 'none'
                                            }}>LOGO</div>

                                            <div id="draggable-name" className="draggable-element" style={{
                                                position: 'absolute',
                                                top: 0, left: 0,
                                                transform: `translate(${pos.name_x / scale}px, ${pos.name_y / scale}px)`,
                                                fontSize: `${pos.name_font_size / scale}px`,
                                                color: pos.name_font_color,
                                                border: '2px dashed rgba(34, 197, 94, 0.8)',
                                                background: 'rgba(34, 197, 94, 0.2)',
                                                padding: '2px 8px',
                                                fontWeight: 'bold', whiteSpace: 'nowrap',
                                                cursor: 'move', touchAction: 'none'
                                            }}>Restaurant Name</div>
                                        </>
                                    )}
                                </div>

                                {/* Controls */}
                                <div className="sa-panel" style={{ width: '100%', maxWidth: '600px', margin: '0 auto' }}>
                                    <h3 className="sa-panel-title"><i className="fas fa-sliders-h"></i> Positioning Attributes</h3>
                                    
                                    <div style={{ marginBottom: '1.5rem' }}>
                                        <h4 style={{ margin: '0 0 10px 0', color: 'var(--primary-orange)' }}>QR Code Box</h4>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <div className="sa-form-group">
                                                <label className="sa-form-label">X Axis</label>
                                                <input type="number" className="sa-input" style={{ width: '100%' }} value={pos.qr_x} onChange={(e) => handlePosChange('qr_x', Number(e.target.value))} />
                                            </div>
                                            <div className="sa-form-group">
                                                <label className="sa-form-label">Y Axis</label>
                                                <input type="number" className="sa-input" style={{ width: '100%' }} value={pos.qr_y} onChange={(e) => handlePosChange('qr_y', Number(e.target.value))} />
                                            </div>
                                        </div>
                                        <div className="sa-form-group">
                                            <label className="sa-form-label">Size (px)</label>
                                            <input type="number" className="sa-input" style={{ width: '100%' }} value={pos.qr_size} onChange={(e) => handlePosChange('qr_size', Number(e.target.value))} />
                                        </div>
                                    </div>
                                    
                                    <div style={{ marginBottom: '1.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem' }}>
                                        <h4 style={{ margin: '0 0 10px 0', color: 'var(--info)' }}>Logo Box</h4>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <div className="sa-form-group">
                                                <label className="sa-form-label">X Axis</label>
                                                <input type="number" className="sa-input" style={{ width: '100%' }} value={pos.logo_x} onChange={(e) => handlePosChange('logo_x', Number(e.target.value))} />
                                            </div>
                                            <div className="sa-form-group">
                                                <label className="sa-form-label">Y Axis</label>
                                                <input type="number" className="sa-input" style={{ width: '100%' }} value={pos.logo_y} onChange={(e) => handlePosChange('logo_y', Number(e.target.value))} />
                                            </div>
                                        </div>
                                        <div className="sa-form-group">
                                            <label className="sa-form-label">Size (px)</label>
                                            <input type="number" className="sa-input" style={{ width: '100%' }} value={pos.logo_size} onChange={(e) => handlePosChange('logo_size', Number(e.target.value))} />
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: '1.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem' }}>
                                        <h4 style={{ margin: '0 0 10px 0', color: 'var(--success)' }}>Restaurant Name Text</h4>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <div className="sa-form-group">
                                                <label className="sa-form-label">X Axis</label>
                                                <input type="number" className="sa-input" style={{ width: '100%' }} value={pos.name_x} onChange={(e) => handlePosChange('name_x', Number(e.target.value))} />
                                            </div>
                                            <div className="sa-form-group">
                                                <label className="sa-form-label">Y Axis</label>
                                                <input type="number" className="sa-input" style={{ width: '100%' }} value={pos.name_y} onChange={(e) => handlePosChange('name_y', Number(e.target.value))} />
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <div className="sa-form-group" style={{ flex: 1 }}>
                                                <label className="sa-form-label">Font Size</label>
                                                <input type="number" className="sa-input" style={{ width: '100%' }} value={pos.name_font_size} onChange={(e) => handlePosChange('name_font_size', Number(e.target.value))} />
                                            </div>
                                            <div className="sa-form-group" style={{ width: '60px' }}>
                                                <label className="sa-form-label">Color</label>
                                                <input type="color" className="sa-input" style={{ width: '100%', padding: '2px', height: '42px', cursor: 'pointer' }} value={pos.name_font_color} onChange={(e) => handlePosChange('name_font_color', e.target.value)} />
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <button onClick={savePositions} disabled={savingPos} className="sa-primary-btn" style={{ width: '100%', padding: '15px' }}>
                                            {savingPos ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : <><i className="fas fa-save"></i> Save Positions to Database</>}
                                        </button>
                                        <button onClick={downloadPreview} className="sa-secondary-btn" style={{ width: '100%', padding: '15px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                                            <i className="fas fa-download"></i> Download Preview Image
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
        </SuperAdminLayout>
    );
};

export default ManageQRTemplates;
